import { useEffect, useState } from 'react';
import { Table, SimpleGrid, Text, Stack, Paper, Group, Button, Badge } from '@mantine/core';
import { IconAdd as IconPlus, IconEndMill } from '../../components/icons/CncIcons';
import EntityHeader from '../../components/EntityHeader';
import { ViewMode } from '../../components/TableGridToggle';
import { api, imageUrl } from '../../api/client';
import EquipmentModal from './EquipmentModal';

export interface Equipment {
  id: number;
  name: string;
  manufacturer: string | null;
  model: string | null;
  imagePath: string | null;
  powerKwh: number | null;
  depreciationPerHour: number | null;
  markupPct: number;
  hourlyRateEur: number | null;
  status: 'Available' | 'Broken' | 'Dead';
}

function statusColor(s: Equipment['status']) {
  return s === 'Available' ? 'green' : s === 'Broken' ? 'yellow' : 'red';
}

export default function EquipmentPage() {
  const [view, setView] = useState<ViewMode>('table');
  const [rows, setRows] = useState<Equipment[]>([]);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);

  async function refresh() {
    const list = await api.get<Equipment[]>('/equipment');
    setRows(list);
  }
  useEffect(() => { refresh(); }, []);

  return (
    <Stack>
      <EntityHeader title="Lab Equipment" icon={<IconEndMill size={26} />} view={view} onView={setView} />

      {view === 'table' ? (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Image</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: 140 }}>Manufacturer</Table.Th>
                <Table.Th style={{ width: 140 }}>Model</Table.Th>
                <Table.Th style={{ width: 120 }}>Cost (EUR)</Table.Th>
                <Table.Th style={{ width: 100 }}>Markup</Table.Th>
                <Table.Th style={{ width: 120 }}>Price (EUR)</Table.Th>
                <Table.Th style={{ width: 120 }}>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const cost = (r.powerKwh ?? 0) + (r.depreciationPerHour ?? 0);
                return (
                  <Table.Tr key={r.id} className="click-row" onClick={() => setEditingId(r.id)}>
                    <Table.Td>
                      <div className="image-box">
                        {r.imagePath && <img src={imageUrl(r.imagePath, true)} alt={r.name} />}
                      </div>
                    </Table.Td>
                    <Table.Td>{r.name}</Table.Td>
                    <Table.Td>{r.manufacturer ?? '—'}</Table.Td>
                    <Table.Td>{r.model ?? '—'}</Table.Td>
                    <Table.Td>{cost > 0 ? cost.toFixed(2) : '—'}</Table.Td>
                    <Table.Td>{r.markupPct}%</Table.Td>
                    <Table.Td>{r.hourlyRateEur != null ? r.hourlyRateEur.toFixed(2) : '—'}</Table.Td>
                    <Table.Td><Badge color={statusColor(r.status)} variant="light">{r.status}</Badge></Table.Td>
                  </Table.Tr>
                );
              })}
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingId('new')} data-tour="equipment-add">
                    Add Equipment
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {rows.map((r) => (
            <div key={r.id} className="grid-card" onClick={() => setEditingId(r.id)}>
              <div className="card-image">
                {r.imagePath ? <img src={imageUrl(r.imagePath)} alt={r.name} /> : <Text c="dimmed" size="xs">No image</Text>}
              </div>
              <Group justify="space-between">
                <Text fw={600}>{r.name}</Text>
                <Badge color={statusColor(r.status)} variant="light">{r.status}</Badge>
              </Group>
              <Text size="sm" c="orange">{r.hourlyRateEur != null ? `€${r.hourlyRateEur.toFixed(2)}/h` : '—'}</Text>
            </div>
          ))}
          <div className="add-card" onClick={() => setEditingId('new')}>
            <Group gap={6}><IconPlus size={16} /><Text>Add Equipment</Text></Group>
          </div>
        </SimpleGrid>
      )}

      {editingId !== null && (
        <EquipmentModal
          id={editingId === 'new' ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={async () => { await refresh(); setEditingId(null); }}
          onDeleted={async () => { await refresh(); setEditingId(null); }}
        />
      )}
    </Stack>
  );
}
